import React, { forwardRef, useContext, useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import "../../../../../App.css";
import { AuthContext } from "../../../../../context/AuthContext";
import { fetchWithAuth } from "../../../../../utils/api";
import Loader from "../../../../ui/Loader";

const formatLatexText = (text) => {
  if (!text || typeof text !== "string") return text;
  let processed = text;
  if (!processed.includes("$") && !processed.includes("\\(") && !processed.includes("\\[")) {
    const words = [];
    let current = "";
    let depth = 0;
    for (let i = 0; i < processed.length; i++) {
      const c = processed[i];
      if (c === "{" || c === "(") depth++;
      else if (c === "}" || c === ")") depth = Math.max(0, depth - 1);
      if (c === " " && depth === 0) {
        if (current) words.push(current);
        current = "";
      } else {
        current += c;
      }
    }
    if (current) words.push(current);
    processed = words.map((word) => (/(\\[a-zA-Z]+|\^|_)/.test(word) ? `$${word}$` : word)).join(" ");
  }
  processed = processed.replace(/(^|[^\\])\$([^$]+)\$/g, (match, before, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `${before}$\\displaystyle ${math}$`;
  });
  processed = processed.replace(/\\\((.*?)\\\)/g, (match, math) => {
    if (math.includes("\\displaystyle")) return match;
    return `\\(\\displaystyle ${math}\\)`;
  });
  return processed;
};

const getMCQOptionText = (question, answer) => {
  if (!answer || !Array.isArray(question?.options)) return answer || "-";

  const keys = (Array.isArray(answer) ? answer : String(answer).split(",")).map(
    (k) => k.trim()
  );

  const mapped = keys.map((key) => {
    const matchedOpt = question.options.find(
      (opt) =>
        opt?.key &&
        String(opt.key).trim().toUpperCase() === String(key).trim().toUpperCase()
    );
    return matchedOpt
      ? matchedOpt.text
      : key;
  });

  return mapped.join(", ");
};

const SPAN_COMPONENTS = { p: "span" };
const REMARK_PLUGINS = [remarkMath, remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [
  [rehypeKatex, { strict: "ignore", macros: { "\\frac": "\\dfrac" } }],
];

const Report = forwardRef(
  (
    { testName, studentName, resultData, instituteInfo: instituteInfoProp },
    ref
  ) => {
    const { user } = useContext(AuthContext);
    const [instituteData, setInstituteData] = useState(null);
    const [logoLoading, setLogoLoading] = useState(true);

    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const totalObtainedScore = resultData.reduce(
      (sum, q) => sum + (q.obtainedScore || 0),
      0
    );
    const totalMaxScore = resultData.reduce(
      (sum, q) => sum + (q.maxScore || 0),
      0
    );
    const scorePercentage = (
      (totalObtainedScore / totalMaxScore) *
      100
    ).toFixed(2);

    useEffect(() => {
      if (instituteInfoProp) {
        setInstituteData(instituteInfoProp);
        setLogoLoading(false);
        return;
      }

      const fetchInstituteData = async () => {
        try {
          setLogoLoading(true);
          if (!user?.instituteId) {
            setLogoLoading(false);
            return;
          }
          const response = await fetchWithAuth(
            `${serverUrl}/api/institute/get/${user.instituteId}`,
            {
              credentials: "include",
            }
          );

          if (response && response.ok) {
            const data = await response.json();
            setInstituteData(data);
          }
        } catch (err) {
          console.error("Error fetching institute data:", err);
        } finally {
          setLogoLoading(false);
        }
      };

      if (user?.instituteId) {
        fetchInstituteData();
      } else {
        setLogoLoading(false);
      }
    }, [serverUrl, user?.instituteId, instituteInfoProp]);

    return (
      <div ref={ref} className="report-container">
        <div className="report-brand">
          <div className="logo-area">
            {!logoLoading && (
              <img
                src={instituteData?.logoURL || "https://res.cloudinary.com/dfdsncmih/image/upload/v1759219779/grademe_ai_logo_sjzbso.png"}
                alt="institute-logo"
                height={50}
                onError={(e) => {
                  e.target.src = "https://res.cloudinary.com/dfdsncmih/image/upload/v1759219779/grademe_ai_logo_sjzbso.png";
                }}
              />
            )}
            {logoLoading && <Loader style={{ left: "0" }} />}
            <h1>{instituteData?.name || "GradeMe AI"}</h1>
            <span className="report-date">
              Date: {new Date().toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="report-header">
          <h2>Test Name: {testName}</h2>
          <div className="student-info">
            <div className="info-item">
              <span className="label">Student Name:</span>
              <span className="value">{studentName}</span>
            </div>
            <div className="info-item score-display">
              <span className="label">Total Score:</span>
              <span className="value">
                {totalObtainedScore}/{totalMaxScore} ({scorePercentage}%)
              </span>
            </div>
          </div>
        </div>

        <div className="results-table">
          <table>
            <thead>
              <tr>
                <th>Q.No</th>
                {resultData.some((q) => q.subject) && <th>Subject</th>}
                {resultData.some((q) => q.sectionNumber) && <th>Section</th>}
                <th>Question</th>
                <th>Answer</th>
                <th>Score</th>
                <th>Feedback</th>
              </tr>
            </thead>
            <tbody>
              {resultData.map((question, index) => (
                <tr key={index}>
                  <td className="question-number">{question.questionNumber}</td>
                  {resultData.some((q) => q.subject) && (
                    <td>{question.subject || "-"}</td>
                  )}
                  {resultData.some((q) => q.sectionNumber) && (
                    <td>{question.sectionNumber || "-"}</td>
                  )}
                  <td className="question-text">
                    <ReactMarkdown
                      remarkPlugins={REMARK_PLUGINS}
                      rehypePlugins={REHYPE_PLUGINS}
                      components={SPAN_COMPONENTS}
                    >
                      {formatLatexText(question.questionText)}
                    </ReactMarkdown>
                  </td>
                  <td className="student-answer">
                    <ReactMarkdown
                      remarkPlugins={REMARK_PLUGINS}
                      rehypePlugins={REHYPE_PLUGINS}
                      components={SPAN_COMPONENTS}
                    >
                      {formatLatexText(getMCQOptionText(question, question.studentAnswer) || "-")}
                    </ReactMarkdown>
                  </td>
                  <td className="score-cell">
                    <span className="score-badge">
                      {question.obtainedScore}/{question.maxScore}
                    </span>
                  </td>
                  <td className="feedback-cell">
                    <ReactMarkdown
                      remarkPlugins={REMARK_PLUGINS}
                      rehypePlugins={REHYPE_PLUGINS}
                      components={SPAN_COMPONENTS}
                    >
                      {formatLatexText(question.feedback || "-")}
                    </ReactMarkdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

export default React.memo(Report);
